export function formatDate(dateStr: string, isDateOnly = false): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return isDateOnly ?
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` :
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function formatAmount(value: number | string | null, currency: string): string {
  if (value === null) return '';

  const num = Number(value);
  if (isNaN(num)) return '';

  // 小数点が必要な場合のみ表示
  return currency === 'JPY'
    ? num.toLocaleString('ja-JP', { maximumFractionDigits: 0 })
    : num.toLocaleString('ja-JP', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function truncateDecimals(value: number, currency: string): number {
  return (currency === 'JPY') ? Math.trunc(value) : Math.floor(value * 100) / 100;
}

export function escapeHTML(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function floor2dpString(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  const s = String(v).trim();
  const m = s.match(/^(-?)(\d+)(?:\.(\d{0,5}))?$/);
  if (!m) return '';

  const negative = m[1] === '-';
  const intPart = m[2];
  const fracRaw = m[3] ?? '';

  // 2桁を基準に判定
  const first2 = (fracRaw + '00').slice(0, 2);
  const rest   = (fracRaw.length > 2) ? fracRaw.slice(2) : '';

  // 正: そのまま / 負: 2桁目以降に非ゼロがあれば 2桁部を -0.01 相当へ
  if (!negative) {
    return `${intPart}.${first2}`;
  } else {
    if (/[^0]/.test(rest)) {
      // 例: -1.23001 → -1.24
      let n = Number(`${intPart}.${first2}`);
      n -= 0.01;
      return n.toFixed(2);
    }
    return `-${intPart}.${first2}`;
  }
}

export function getAdToYm(ad: number) {
  if (ad === 0) return '';
  const y = ad.toString().substring(0, 4);
  const m = ad.toString().substring(4, 6);
  return `${y}年${m}月`;
}

export function getPrevAd(ad: number) {
    const y = parseInt(ad.toString().substring(0, 4));
    const m = parseInt(ad.toString().substring(4, 6));
    return (m ===  1) ? (y - 1) * 100 + 12 : y * 100 + (m - 1);
}

export function getNextAd(ad: number) {
    const y = parseInt(ad.toString().substring(0, 4));
    const m = parseInt(ad.toString().substring(4, 6));
    return (m === 12) ? (y + 1) * 100 + 1  : y * 100 + (m + 1);
}

/**
 * DB の DECIMAL(x,y) 文字列/数値を表示用に整形。
 * - 小数部は末尾の 0 をすべて削除（丸めなし）
 * - 小数部が空になったら小数点ごと消す
 * - 整数部は 3桁区切り
 * - 不正値/空は "" を返す
 */
export function formatDecimalTrim(v: unknown, locale = 'ja-JP', scale = 2): string {
  if (v === null || v === undefined || v === '') return '';

  const s = String(v).trim();
  const m = s.match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!m) return '';

  const sign = m[1] ?? '';
  const intPart = m[2];
  let frac = m[3] ?? '';

  // 小数部を最大 scale 桁に切り捨て
  if (scale >= 0 && frac.length > scale) {
    frac = frac.slice(0, scale);
  }

  // 小数部の末尾 0 を削除
  frac = frac.replace(/0+$/, '');

  // 整数部を桁区切り
  let intFormatted: string;
  if (intPart.length <= 15) {
    intFormatted = new Intl.NumberFormat(locale, { useGrouping: true }).format(Number(intPart));
  } else {
    intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  return frac ? `${sign}${intFormatted}.${frac}` : `${sign}${intFormatted}`;
}

// 文字列/空を数値へ（空/nullはnullに）
export function toNum(v: any, isNegative = false): number | null {
  if (v === null || v === undefined || v === '') return null;
  return Number(String(v).replace(/,/g, '')); // 念のためカンマ除去
}

// 数値比較コンパレータ（null/空は最後）
export function numericComparator(a: any, b: any): number {
  const na = toNum(a), nb = toNum(b);
  if (na == null && nb == null) return 0;
  if (na == null) return 1;
  if (nb == null) return -1;
  return na - nb;
}

import type { ICellRendererParams } from 'ag-grid-community';
import { PoolTypeMap } from '../models/weekly-report.model';

export function decimalCellRenderer(params: ICellRendererParams) {
  const negative = (parseFloat(params.value) < 0) ? 'negative' : '';
  const text = formatDecimalTrim(params.value, 'ja-JP');
  if (!text) return '';
  const [intPart, frac] = text.split('.');
  // HTMLを返す（ag-Gridは文字列なら innerHTML で描画）
  return `
    <span class="dec int ${negative}">${intPart ?? ''}</span>
    ${frac ? `<span class="dec dot ${negative}">.</span><span class="dec frac ${negative}">${frac}</span>` : ''}
  `;
}

export function decimalCellRendererSuffixPer(params: ICellRendererParams) {
  const negative = (parseFloat(params.value) < 0) ? 'negative' : '';
  const text = formatDecimalTrim(params.value, 'ja-JP');
  if (!text) return '';
  const [intPart, frac] = text.split('.');
  // HTMLを返す（ag-Gridは文字列なら innerHTML で描画）
  return `
    <span class="dec int ${negative}">${intPart ?? ''}</span>
    ${frac ? `<span class="dec dot ${negative}">.</span><span class="dec frac ${negative}">${frac}</span>` : ''}
  ` + '%';
}

export function decimalPercentCellRenderer(params: ICellRendererParams) {
  const negative = (parseFloat(params.value) < 0) ? 'negative' : '';
  const text = formatDecimalTrim(parseFloat(params.value) * 100, 'ja-JP');
  if (!text) return '';
  const [intPart, frac] = text.split('.');
  // HTMLを返す（ag-Gridは文字列なら innerHTML で描画）
  return `
    <span class="dec int ${negative}">${intPart ?? ''}</span>
    ${frac ? `<span class="dec dot ${negative}">.</span><span class="dec frac ${negative}">${frac}</span>` : ''}
  ` + '%';
}

export function blankOpen(e: Event|undefined, uri: string) {
  if (e) e.stopPropagation();
  window.open(uri, '_blank');
}

export function getPool(cap_id: string) {
  const obj = PoolTypeMap.find(x => x.acc === cap_id.substring(0, x.acc.length));
  return obj ? obj.pool : '不明';
}

export function percentToRate(percent: number, scale = 4): number {
  const factor = 10 ** scale;
  return Math.round((percent / 100) * factor) / factor;
}

export function rateToPercent(rate: number, scale = 2): number {
  const factor = 10 ** scale;
  return Math.round(rate * 100 * factor) / factor;
}

export function nowTimestamp(): string {
  const d = new Date();

  const pad = (n: number) => n.toString().padStart(2, '0');

  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  const ss = pad(d.getSeconds());

  return `${yyyy}${mm}${dd}_${hh}${mi}${ss}`;
}

export function compareAccountNo(a: any, b: any): number {
    const A = (a ?? '').toString().trim();
    const B = (b ?? '').toString().trim();

    const parse = (s: string) => {
      const m = s.match(/^(.+?)-(\d+)$/);
      if (!m) return { base: s, branch: Number.NaN };
      return { base: m[1], branch: Number(m[2]) };
    };

    const pa = parse(A);
    const pb = parse(B);

    // base が違うなら文字列比較（必要なら localeCompare）
    if (pa.base !== pb.base) return pa.base.localeCompare(pb.base);

    // 枝番が数字なら数値比較（NaN は末尾へ）
    const aNaN = Number.isNaN(pa.branch);
    const bNaN = Number.isNaN(pb.branch);
    if (aNaN && bNaN) return 0;
    if (aNaN) return 1;
    if (bNaN) return -1;

    return pa.branch - pb.branch;
  };
